import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { alpacaService } from '../../services/AlpacaService';
import type { ScreenerResult, ScreenerResponse, MarketRegime, SavedScan } from '../../services/AlpacaService';
import { useToast } from '../../context/ToastContext';
import styles from './Screener.module.css';

const CONSENSUS_LABELS: Record<string, { label: string; color: string }> = {
  BOTH_AGREE: { label: 'Both Agree', color: '#27ae60' },
  LEAN_AGREE: { label: 'Lean Agree', color: '#f39c12' },
  CLAUDE_ONLY: { label: 'Claude Only', color: '#8b5cf6' },
  GPT_ONLY: { label: 'GPT Only', color: '#34d399' },
};

const Screener: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [sectors, setSectors] = useState<Record<string, string[]>>({});
  const [selectedSector, setSelectedSector] = useState('');
  const [customSymbols, setCustomSymbols] = useState('');
  const [minConfidence, setMinConfidence] = useState(50);
  const [aiMode, setAiMode] = useState<'claude' | 'gpt' | 'both'>('claude');
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState<ScreenerResult[]>([]);
  const [meta, setMeta] = useState<{
    scannedCount: number;
    consensusCount: number;
    earningsBlockedCount?: number;
    scannedAt: string;
    regime?: MarketRegime;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [savedScans, setSavedScans] = useState<SavedScan[]>([]);
  const [activeScanId, setActiveScanId] = useState<string | null>(null);
  const [savingScan, setSavingScan] = useState(false);

  const toggleRow = (symbol: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(symbol)) next.delete(symbol);
      else next.add(symbol);
      return next;
    });
  };

  // Load saved scans on mount — survives tab refreshes
  useEffect(() => {
    alpacaService
      .getSavedScans()
      .then((data) => setSavedScans(data.scans || []))
      .catch(() => {
        /* non-critical */
      });
  }, []);

  const applySavedScan = useCallback((scan: SavedScan) => {
    setActiveScanId(scan.id);
    setSelectedSector(scan.sector || '');
    setCustomSymbols(scan.customSymbols || '');
    setMinConfidence(scan.minConfidence);
    setAiMode(scan.aiMode);
  }, []);

  const handleSaveScan = useCallback(async () => {
    const existing = activeScanId ? savedScans.find((s) => s.id === activeScanId) : null;
    const defaultName = existing
      ? existing.name
      : `${selectedSector || customSymbols.split(',')[0]?.trim() || 'Scan'} · ${minConfidence}%+`;
    const name = window.prompt('Name for this saved scan:', defaultName || '');
    if (!name || !name.trim()) return;
    const wantsAlert = window.confirm(
      'Alert me when NEW tickers match this scan? (SMS/email during market hours — requires notification prefs to be configured)',
    );
    setSavingScan(true);
    try {
      const res = await alpacaService.saveScan({
        id: activeScanId || undefined,
        name: name.trim(),
        sector: selectedSector || null,
        customSymbols: customSymbols.trim() || null,
        minConfidence,
        aiMode,
        notifyOnMatch: wantsAlert,
        notifyChannels: existing?.notifyChannels || { sms: true, email: true },
      });
      if (res.success && res.scan) {
        setActiveScanId(res.scan.id);
        const list = await alpacaService.getSavedScans();
        setSavedScans(list.scans || []);
        addToast(`Saved "${res.scan.name}"${wantsAlert ? ' — alerts on' : ''}`, 'success');
      }
    } catch (err: any) {
      addToast(err.message || 'Failed to save scan', 'error');
    } finally {
      setSavingScan(false);
    }
  }, [activeScanId, customSymbols, selectedSector, minConfidence, aiMode, savedScans, addToast]);

  const handleDeleteSavedScan = useCallback(
    async (scan: SavedScan) => {
      if (!window.confirm(`Delete saved scan "${scan.name}"?`)) return;
      try {
        await alpacaService.deleteSavedScan(scan.id);
        setSavedScans((prev) => prev.filter((s) => s.id !== scan.id));
        if (activeScanId === scan.id) setActiveScanId(null);
        addToast(`Deleted "${scan.name}"`, 'success');
      } catch (err: any) {
        addToast(err.message || 'Failed to delete scan', 'error');
      }
    },
    [activeScanId, addToast],
  );

  // Load sector list on mount
  useEffect(() => {
    alpacaService
      .getSectorList()
      .then((data) => setSectors(data.sectors || {}))
      .catch(() => {
        /* sectors will load with first scan result */
      });
  }, []);

  const handleScan = useCallback(async () => {
    setScanning(true);
    setError(null);
    setResults([]);
    setMeta(null);

    try {
      const symbols = customSymbols
        .split(',')
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);

      const data: ScreenerResponse = await alpacaService.runScreener(
        symbols,
        selectedSector || undefined,
        minConfidence,
        aiMode,
      );

      setResults(data.results || []);
      setMeta({
        scannedCount: data.scannedCount,
        consensusCount: data.consensusCount,
        earningsBlockedCount: data.earningsBlockedCount,
        scannedAt: data.scannedAt,
        regime: data.regime,
      });

      // Update sectors from response if we got them
      if (data.sectors && Object.keys(data.sectors).length > 0) {
        setSectors(data.sectors);
      }

      if (data.partial) {
        addToast(
          `Partial results — scan timed out at ~25s. Showing ${data.results.length} of ${data.scannedCount}+. Try a smaller sector or higher confidence.`,
          'info',
        );
      } else if (data.results.length === 0) {
        addToast('No results found — try lowering the confidence threshold', 'info');
      } else {
        const bothAgree = data.results.filter((r) => r.consensusTag === 'BOTH_AGREE').length;
        addToast(
          `Found ${data.results.length} picks (${bothAgree} full consensus)`,
          bothAgree > 0 ? 'success' : 'info',
        );
      }
    } catch (err: any) {
      setError(err.message || 'Screener failed');
      addToast(err.message || 'Screener failed', 'error');
    } finally {
      setScanning(false);
    }
  }, [selectedSector, customSymbols, minConfidence, aiMode, addToast]);

  const handleTrade = (result: ScreenerResult) => {
    const action = result.consensus === 'LONG' ? 'buy' : 'short';
    const params = new URLSearchParams({ symbol: result.symbol, action });
    if (result.claude.stopLoss != null) params.set('sl', String(result.claude.stopLoss));
    if (result.claude.takeProfit != null) params.set('tp', String(result.claude.takeProfit));
    if (result.claude.holdDays != null) params.set('hold', String(result.claude.holdDays));
    if (result.claude.profitTargetPct != null) params.set('tgtPct', String(result.claude.profitTargetPct));
    params.set('plan', 'ai');
    navigate(`/quick-trade?${params.toString()}`);
  };

  const confidenceClass = (conf: number) => {
    if (conf >= 70) return styles.confidenceHigh;
    if (conf >= 50) return styles.confidenceMed;
    return styles.confidenceLow;
  };

  const canScan = !scanning && (selectedSector || customSymbols.trim());

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>AI Screener</h1>
      </div>
      <p className={styles.subtitle}>Claude + GPT independently analyze stocks — results ranked by consensus</p>

      {/* ── Controls ── */}
      <div className={styles.controls}>
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Sector</span>
          <select
            className={styles.sectorSelect}
            value={selectedSector}
            onChange={(e) => {
              setSelectedSector(e.target.value);
              if (e.target.value) setCustomSymbols('');
            }}
          >
            <option value="">Select a sector...</option>
            <option value="all">All Sectors ({Object.values(sectors).reduce((n, s) => n + s.length, 0)})</option>
            {Object.keys(sectors)
              .sort()
              .map((sector) => (
                <option key={sector} value={sector}>
                  {sector} ({sectors[sector].length})
                </option>
              ))}
          </select>
        </div>

        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Or custom tickers</span>
          <input
            className={styles.customInput}
            type="text"
            placeholder="AAPL, NVDA, TSLA, META..."
            value={customSymbols}
            onChange={(e) => {
              setCustomSymbols(e.target.value);
              if (e.target.value.trim()) setSelectedSector('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canScan) handleScan();
            }}
          />
        </div>

        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Min Confidence</span>
          <select
            className={styles.sectorSelect}
            value={minConfidence}
            onChange={(e) => setMinConfidence(Number(e.target.value))}
            style={{ minWidth: 100 }}
          >
            <option value={30}>30%</option>
            <option value={40}>40%</option>
            <option value={50}>50%</option>
            <option value={60}>60%</option>
            <option value={70}>70%</option>
            <option value={80}>80%</option>
            <option value={90}>90%</option>
            <option value={95}>95%</option>
          </select>
        </div>

        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>AI Engine</span>
          <select
            className={styles.sectorSelect}
            value={aiMode}
            onChange={(e) => setAiMode(e.target.value as 'claude' | 'gpt' | 'both')}
            style={{ minWidth: 130 }}
          >
            <option value="claude">Claude Only</option>
            <option value="gpt">GPT Only</option>
            <option value="both">Both (Consensus)</option>
          </select>
        </div>

        <button className={styles.scanButton} onClick={handleScan} disabled={!canScan}>
          {scanning ? 'Scanning...' : 'Scan'}
        </button>
        <button
          className={styles.scanButton}
          onClick={handleSaveScan}
          disabled={savingScan || !canScan}
          style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6', borderColor: 'rgba(139, 92, 246, 0.4)' }}
          title={activeScanId ? 'Update this saved scan' : 'Save this scan config for one-click re-run'}
        >
          {savingScan ? 'Saving…' : activeScanId ? 'Update' : 'Save'}
        </button>
      </div>

      {/* ── Saved scans row ── */}
      {savedScans.length > 0 && (
        <div className={styles.savedScansRow}>
          <span className={styles.savedScansLabel}>Saved:</span>
          {savedScans.map((scan) => {
            const active = scan.id === activeScanId;
            return (
              <span
                key={scan.id}
                className={active ? styles.savedScanChipActive : styles.savedScanChip}
                onClick={() => applySavedScan(scan)}
                title={
                  (scan.sector ? `Sector: ${scan.sector}` : `Tickers: ${scan.customSymbols}`) +
                  ` · ${scan.minConfidence}%+ · ${scan.aiMode}`
                }
              >
                {scan.name}
                {scan.notifyOnMatch && (
                  <span
                    title="Alerts on — SMS/email fires when NEW tickers match this scan"
                    style={{ fontSize: '0.7rem', marginLeft: '4px' }}
                  >
                    🔔
                  </span>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSavedScan(scan);
                  }}
                  className={styles.savedScanDelete}
                  title={`Delete "${scan.name}"`}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* ── Scanning Progress ── */}
      {scanning && (
        <div className={styles.progress}>
          <div className={styles.progressSpinner} />
          <p className={styles.progressText}>Scanning {selectedSector || 'custom tickers'} through Claude + GPT...</p>
        </div>
      )}

      {/* ── Error ── */}
      {error && <div className={styles.errorState}>{error}</div>}

      {/* ── Regime banner ── */}
      {meta?.regime && !scanning && (
        <div
          className={styles.regimeBanner}
          data-regime={meta.regime.name}
          title={`${meta.regime.bias} ${meta.regime.weights}`}
        >
          <span className={styles.regimeName}>{meta.regime.name.replace(/_/g, ' ')}</span>
          <span className={styles.regimeBias}>{meta.regime.bias}</span>
          <span className={styles.regimeStats}>
            mood {meta.regime.mood_score} · geo {meta.regime.geo_risk}
          </span>
        </div>
      )}

      {/* ── Results Summary ── */}
      {meta && !scanning && (
        <div className={styles.summary}>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>Scanned</span>
            <span className={styles.summaryValue}>{meta.scannedCount}</span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>Results</span>
            <span className={styles.summaryValueGreen}>{results.length}</span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>Both Agree</span>
            <span className={styles.summaryValueGreen}>
              {results.filter((r) => r.consensusTag === 'BOTH_AGREE').length}
            </span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>Hit Rate</span>
            <span className={styles.summaryValue}>
              {meta.scannedCount > 0 ? Math.round((results.length / meta.scannedCount) * 100) : 0}%
            </span>
          </div>
          {meta.earningsBlockedCount != null && meta.earningsBlockedCount > 0 && (
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>Earnings Blocked</span>
              <span className={styles.summaryValue}>{meta.earningsBlockedCount}</span>
            </div>
          )}
        </div>
      )}

      {/* ── Results Table ── */}
      {results.length > 0 && !scanning && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Consensus</th>
                <th>Direction</th>
                <th>Price</th>
                <th>RSI(2)</th>
                <th>RSI(6)</th>
                <th>
                  <span className={styles.badgeClaude}>Claude</span>
                </th>
                <th>
                  <span className={styles.badgeGpt}>GPT</span>
                </th>
                <th>Combined</th>
                <th>Hold</th>
                <th>Target</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => {
                const tag = r.consensusTag || 'CLAUDE_ONLY';
                const tagInfo = CONSENSUS_LABELS[tag] || CONSENSUS_LABELS.CLAUDE_ONLY;
                const isExpanded = expandedRows.has(r.symbol);
                return (
                  <React.Fragment key={r.symbol}>
                    <tr className={styles.clickableRow} onClick={() => toggleRow(r.symbol)}>
                      <td>
                        <span className={styles.symbolCell}>{r.symbol}</span>
                        {r.earningsStatus === 'WARN' && (
                          <span className={styles.earningsBadge} title={r.earningsMessage || 'Earnings approaching'}>
                            ER {r.earningsTradingDays}d
                          </span>
                        )}
                        {r.intraday?.alignment === 'ALIGNED' && (
                          <span
                            className={styles.alignBadgeYes}
                            title={`1H ${r.intraday.ema_trend} agrees with daily — RSI ${r.intraday.rsi14 ?? '—'}, momentum ${r.intraday.momentum_pct}%`}
                          >
                            ✓ 1H
                          </span>
                        )}
                        {r.intraday?.alignment === 'DIVERGING' && (
                          <span
                            className={styles.alignBadgeNo}
                            title={`Daily trend and 1H trend disagree — RSI ${r.intraday.rsi14 ?? '—'}, momentum ${r.intraday.momentum_pct}%`}
                          >
                            ⚠ 1H
                          </span>
                        )}
                        {r.options && (
                          <span
                            className={
                              r.options.iv_class === 'HIGH'
                                ? styles.ivBadgeHigh
                                : r.options.iv_class === 'LOW'
                                  ? styles.ivBadgeLow
                                  : styles.ivBadgeNormal
                            }
                            title={`Options ${r.options.near_dte}DTE: ATM IV ${r.options.atm_iv_pct}% (${r.options.iv_class}) · P/C ${r.options.put_call_ratio ?? '—'} (${r.options.pc_signal})`}
                          >
                            IV {Math.round(r.options.atm_iv_pct)}
                          </span>
                        )}
                        {r.options?.pc_signal === 'BULLISH' && r.options.put_call_ratio != null && (
                          <span
                            className={styles.pcBadgeBull}
                            title={`Put/Call OI ratio ${r.options.put_call_ratio} — call-heavy (bullish positioning)`}
                          >
                            P/C {r.options.put_call_ratio}
                          </span>
                        )}
                        {r.options?.pc_signal === 'BEARISH' && r.options.put_call_ratio != null && (
                          <span
                            className={styles.pcBadgeBear}
                            title={`Put/Call OI ratio ${r.options.put_call_ratio} — put-heavy (bearish positioning)`}
                          >
                            P/C {r.options.put_call_ratio}
                          </span>
                        )}
                        {r.setupHistory && r.setupHistory.triggers >= 5 && (
                          <span
                            className={
                              r.setupHistory.hit_rate >= 60
                                ? styles.histBadgeStrong
                                : r.setupHistory.hit_rate >= 45
                                  ? styles.histBadgeMid
                                  : styles.histBadgeWeak
                            }
                            title={`Backtest: ${r.setupHistory.signal} on ${r.symbol} hit ${r.setupHistory.hits}/${r.setupHistory.triggers} (${r.setupHistory.hit_rate}%) over ${r.setupHistory.lookback_bars} bars · avg ${r.setupHistory.forward_days}-day move ${r.setupHistory.avg_return_pct}% · counts as hit when move ≥ ${r.setupHistory.threshold_pct}% in the predicted direction`}
                          >
                            {r.setupHistory.hits}/{r.setupHistory.triggers}
                          </span>
                        )}
                        {r.shortInterest &&
                          r.shortInterest.bucket !== 'NORMAL' &&
                          (() => {
                            const si = r.shortInterest!;
                            const cls =
                              si.bucket === 'HIGH'
                                ? styles.siBadgeHigh
                                : si.bucket === 'ELEVATED'
                                  ? styles.siBadgeElev
                                  : styles.siBadgeLow;
                            const bucketMsg =
                              si.bucket === 'HIGH'
                                ? 'Squeeze risk on longs, crowded-trade risk on shorts.'
                                : si.bucket === 'ELEVATED'
                                  ? 'Watch for short-covering rallies on catalysts.'
                                  : 'Clean float — no squeeze fuel, shorts viable without crowded-trade fear.';
                            const changeStr =
                              si.pct_change != null
                                ? ` · ΔSI ${si.pct_change >= 0 ? '+' : ''}${si.pct_change}% vs last report`
                                : '';
                            return (
                              <span
                                className={cls}
                                title={`Short interest: ${si.pct_float ?? '?'}% of float, ${si.days_to_cover ?? '?'} days to cover (${si.bucket})${changeStr}. ${bucketMsg}`}
                              >
                                SI{' '}
                                {si.pct_float != null
                                  ? `${Math.round(si.pct_float)}%`
                                  : si.days_to_cover != null
                                    ? `${si.days_to_cover}d`
                                    : '!'}
                              </span>
                            );
                          })()}
                        <br />
                        <span className={styles.sectorCell}>{r.sector}</span>
                      </td>
                      <td>
                        <span
                          className={styles.aiBadge}
                          style={{ background: `${tagInfo.color}22`, color: tagInfo.color }}
                        >
                          {tagInfo.label}
                        </span>
                      </td>
                      <td>
                        <span className={r.consensus === 'LONG' ? styles.directionLong : styles.directionShort}>
                          {r.consensus}
                        </span>
                      </td>
                      <td className={styles.priceCell}>${r.price.toFixed(2)}</td>
                      <td className={styles.priceCell}>{r.rsi2 != null ? r.rsi2.toFixed(1) : '\u2014'}</td>
                      <td className={styles.priceCell}>{r.rsi6 != null ? r.rsi6.toFixed(1) : '\u2014'}</td>
                      <td>
                        <span className={`${styles.confidenceValue} ${confidenceClass(r.claude.confidence)}`}>
                          {r.claude.confidence}%
                        </span>
                      </td>
                      <td>
                        <span className={`${styles.confidenceValue} ${confidenceClass(r.gpt.confidence)}`}>
                          {r.gpt.confidence}%
                        </span>
                      </td>
                      <td>
                        <span className={`${styles.confidenceValue} ${confidenceClass(r.combinedConfidence / 2)}`}>
                          {r.combinedConfidence}
                        </span>
                      </td>
                      <td>{r.claude.holdDays ? `${r.claude.holdDays}d` : '\u2014'}</td>
                      <td>
                        {r.claude.profitTargetPct != null && (
                          <div className={styles.confidenceHigh}>{r.claude.profitTargetPct}%</div>
                        )}
                        {(r.claude.takeProfit != null || r.claude.stopLoss != null) && (
                          <div className={styles.planPrices}>
                            {r.claude.takeProfit != null && <span>TP ${r.claude.takeProfit.toFixed(2)}</span>}
                            {r.claude.stopLoss != null && (
                              <span className={styles.planStop}> · SL ${r.claude.stopLoss.toFixed(2)}</span>
                            )}
                          </div>
                        )}
                        {r.claude.profitTargetPct == null &&
                          r.claude.takeProfit == null &&
                          r.claude.stopLoss == null &&
                          '\u2014'}
                      </td>
                      <td>
                        <button
                          className={styles.tradeButton}
                          title={
                            r.claude.stopLoss || r.claude.takeProfit
                              ? `Open QuickTrade with AI plan: ${r.consensus} · Stop ${r.claude.stopLoss ? '$' + r.claude.stopLoss.toFixed(2) : '—'} · Target ${r.claude.takeProfit ? '$' + r.claude.takeProfit.toFixed(2) : '—'}${r.claude.holdDays ? ' · Hold ' + r.claude.holdDays + 'd' : ''}`
                              : 'Open QuickTrade'
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTrade(r);
                          }}
                        >
                          Trade
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className={styles.detailRow}>
                        <td colSpan={11}>
                          <div className={styles.detailCard}>
                            <div className={styles.detailReason}>{r.claude.reason}</div>
                            {r.earningsStatus === 'WARN' && r.earningsMessage && (
                              <div className={styles.earningsWarning}>
                                ⚠️ {r.earningsMessage}
                                {r.earningsDate ? ` — report ${r.earningsDate}` : ''}
                              </div>
                            )}
                            {r.earningsStatus !== 'WARN' && r.claude.earningsWarning && (
                              <div className={styles.earningsWarning}>{r.claude.earningsWarning}</div>
                            )}
                            {r.options && (
                              <div className={styles.intradayRow}>
                                <strong>Options:</strong> ATM IV {r.options.atm_iv_pct}% ({r.options.iv_class}) · P/C
                                ratio {r.options.put_call_ratio ?? '—'} ({r.options.pc_signal}) · nearest expiry{' '}
                                {r.options.near_expiry} ({r.options.near_dte}d) · {r.options.contracts_seen} contracts
                              </div>
                            )}
                            {r.shortInterest && (
                              <div className={styles.intradayRow}>
                                <strong>Short Interest:</strong>{' '}
                                {r.shortInterest.pct_float != null ? `${r.shortInterest.pct_float}% of float` : '—'} ·
                                days-to-cover{' '}
                                {r.shortInterest.days_to_cover != null ? r.shortInterest.days_to_cover : '—'} · bucket{' '}
                                <strong>{r.shortInterest.bucket}</strong>
                              </div>
                            )}
                            {r.setupHistory && (
                              <div className={styles.intradayRow}>
                                <strong>Backtest ({r.setupHistory.signal}):</strong>{' '}
                                <strong>
                                  {r.setupHistory.hits}/{r.setupHistory.triggers}
                                </strong>{' '}
                                hit rate{' '}
                                <strong
                                  className={
                                    r.setupHistory.hit_rate >= 60
                                      ? styles.alignAligned
                                      : r.setupHistory.hit_rate <= 35
                                        ? styles.alignDiverging
                                        : styles.alignNeutral
                                  }
                                >
                                  ({r.setupHistory.hit_rate}%)
                                </strong>{' '}
                                over the last {r.setupHistory.lookback_bars} bars · avg {r.setupHistory.forward_days}
                                -day move {r.setupHistory.avg_return_pct}% · recent {r.setupHistory.recent_hits}/
                                {r.setupHistory.recent_window} hit
                              </div>
                            )}
                            {r.intraday && (
                              <div className={styles.intradayRow}>
                                <strong>1H:</strong> RSI {r.intraday.rsi14 ?? '—'} · trend {r.intraday.ema_trend} · rel
                                vol {r.intraday.rel_vol}x · 20-bar momentum {r.intraday.momentum_pct}% · range pos{' '}
                                {r.intraday.range_position}% ·{' '}
                                <span
                                  className={
                                    r.intraday.alignment === 'ALIGNED'
                                      ? styles.alignAligned
                                      : r.intraday.alignment === 'DIVERGING'
                                        ? styles.alignDiverging
                                        : styles.alignNeutral
                                  }
                                >
                                  {r.intraday.alignment}
                                </span>
                              </div>
                            )}
                            <div className={styles.detailMeta}>
                              {r.claude.stopLoss && (
                                <span>
                                  SL: <strong>${r.claude.stopLoss.toFixed(2)}</strong>
                                </span>
                              )}
                              {r.claude.takeProfit && (
                                <span>
                                  TP: <strong>${r.claude.takeProfit.toFixed(2)}</strong>
                                </span>
                              )}
                              {r.gpt.reason && (
                                <span>
                                  <span className={styles.badgeGpt}>GPT</span> {r.gpt.reason}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Empty State ── */}
      {meta && results.length === 0 && !scanning && (
        <div className={styles.emptyState}>
          <p className={styles.emptyTitle}>No results found</p>
          <p className={styles.emptyDesc}>
            No tickers passed the {minConfidence}% confidence threshold. Try lowering it or scanning a different sector.
          </p>
        </div>
      )}
    </div>
  );
};

export default Screener;
